import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export interface SubscriptionInfo {
  subscribed: boolean | null;
  status: string | null;
  plan?: string | null;
  monthly_credits?: number | null;
  monthly_searches_used?: number | null;
  trial_expires_at?: string | null;
  subscription_end?: string | null;
  ab_test_group?: string | null;
  auto_discount_code?: string | null;
  auto_discount_expires_at?: string | null;
  trial_reset_2026?: boolean | null;
}

export const getTrialStatus = (subscription: SubscriptionInfo | null) => {
  const isFree = subscription?.plan === 'free' && !subscription?.subscribed;
  const trialExpiry = subscription?.trial_expires_at;
  
  if (!isFree || !trialExpiry) {
    return { isTrialActive: false, isTrialExpired: false, daysLeft: 0 };
  }
  
  const now = new Date();
  const expiryDate = new Date(trialExpiry);
  const isTrialActive = expiryDate > now;
  const isTrialExpired = expiryDate <= now;
  const daysLeft = Math.max(0, Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  
  return { isTrialActive, isTrialExpired, daysLeft };
};

export const getABTestStatus = (subscription: SubscriptionInfo | null) => {
  // Default to 'trial' if group is null (backwards compatibility for existing users)
  const group = subscription?.ab_test_group || 'trial';
  const isDiscountGroup = group === 'discount';
  const discountCode = subscription?.auto_discount_code;
  const discountExpiry = subscription?.auto_discount_expires_at;
  
  let discountActive = false;
  let discountHoursLeft = 0;
  
  if (isDiscountGroup && discountExpiry) {
    const now = new Date();
    const expiry = new Date(discountExpiry);
    discountActive = expiry > now;
    discountHoursLeft = Math.max(0, Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60)));
  }
  
  return { 
    group, 
    isDiscountGroup, 
    discountCode, 
    discountActive, 
    discountHoursLeft,
    discountExpiry
  };
};

export const FREE_TIER_SEARCH_LIMIT = 5;

// Mercado Pago's effective minimum per-charge amount (ARS) for recurring preapprovals.
// MP rejects preapproval creation when auto_recurring.transaction_amount falls below this.
// Kept in a single place so the edge function and UI stay in sync.
export const MP_MIN_RECURRING_ARS = 500;

export const MP_MIN_AMOUNT_MESSAGE =
  "Mercado Pago no permite cobros recurrentes por debajo de $500 ARS. Probá una facturación semestral, un plan superior, o un código de descuento menor.";

export const getFreeTierSearchStatus = (subscription: SubscriptionInfo | null) => {
  const { isTrialExpired } = getTrialStatus(subscription);
  const isTrialGroup = subscription?.ab_test_group === 'trial';
  const isFreeExpiredTrial = subscription?.plan === 'free' && isTrialExpired && isTrialGroup;
  
  const searchesUsed = subscription?.monthly_searches_used ?? 0;
  const searchesRemaining = Math.max(0, FREE_TIER_SEARCH_LIMIT - searchesUsed);
  const limitReached = searchesUsed >= FREE_TIER_SEARCH_LIMIT;
  
  return {
    isFreeExpiredTrial,
    searchesUsed,
    searchesRemaining,
    limit: FREE_TIER_SEARCH_LIMIT,
    limitReached
  };
};
